// import packages
import React, { useEffect, useState } from "react"
import { useQuery, useMutation } from "@apollo/react-hooks"
import { CSVLink } from "react-csv"
import ReactTable from "react-table"
import { Link } from "react-router-dom"
import PropTypes from "prop-types"
import { Spinner } from "components/spinner"
import QueryPanel, { useQueryVariables } from "components/query-panel"
import { Card, CardBody, Button, Popover, PopoverBody } from "reactstrap"
import _ from "lodash"
import gql from "graphql-tag"
import UploadSection from "modules/data-import/UploadSection"
import withModal from "../../modal"
import {
  CreateButton,
  TrashButton,
  SettingButton,
  TableController
} from "../index"

const GET_UNDEFINED_FIELDS = gql`
  query fields {
    fields @client
  }
`

const GET_UNDEFINED_MUTATION = gql`
  mutation fields {
    fields @client
  }
`

const CustomServerSideReactTableNew = ({
  columnDefs,
  modal,
  deleteObjectGql,
  sumTotalQueryObjectGql,
  queryObjectGql,
  fieldsDefGql,
  objectName,
  objectRoute,
  fieldsDef,
  hasCreateButton,
  hasDeleteButton,
  hasEditButton,
  hasSettingButton,
  hasDownloadButton,
  hasUploadButton,
  showPagination,
  defaultPageSize,
  columStyle,
  uploadSettings,
  take = 10
}) => {
  const { data, loading, refetch } = useQuery(queryObjectGql, {
    fetchPolicy: "no-cache",
    variables: { take, skip: 0, input: {} }
  })

  const { data: sumData, refetch: refetchSumData } = useQuery(
    sumTotalQueryObjectGql,
    {
      fetchPolicy: "network-only",
      variables: { input: {} }
    }
  )

  const { data: fields } = useQuery(fieldsDefGql || GET_UNDEFINED_FIELDS)
  const [toggle, setToggle] = useState(false)
  // const [sortedData, setSortedData] = useState([])
  const [deleteObject] = useMutation(deleteObjectGql || GET_UNDEFINED_MUTATION)
  const [refresh, setRefresh] = useState(0)
  const [pageIndex, setPageIndex] = useState(0)

  const getFetchData = (state) => {
    try {
      setPageIndex(state.page)
      refetch({ take, skip: 10 * state?.page })
    } catch (err) {
      console.log("err", err)
    }
  }

  const showUploadModal = (callback) => {
    modal.reset()
    modal.setOnSubmit(callback)
    modal.setHeaderText("匯入資料")
    modal.setSubmitText("關閉")
    modal.setHideCancel(true)
    modal.setModalSize("m")
    modal.setModalContent(
      <UploadSection
        typeName={uploadSettings.typeName}
        description={uploadSettings.description}
        sampleUrl={uploadSettings.sampleUrl}
        uploadType={uploadSettings.uploadType}
      />
    )
    modal.toggle()
  }

  const {
    // options: expectAmountOption,
    variables,
    onClear,
    ...queryPanelProps
  } = useQueryVariables({
    columnDefs
    // optionsQuery: [data]
  })

  const renderCreateButton = () => {
    return (
      <Link to={`${objectRoute}/detail`}>
        <CreateButton>新增{objectName}</CreateButton>
      </Link>
    )
  }

  const renderSettingButton = () => {
    return <SettingButton id="setting" style={{ marginLeft: "auto" }} />
  }

  const renderUploadButton = () => {
    return (
      <Button
        onClick={(e) => {
          e.preventDefault()
          showUploadModal(() => {
            refetch({ take, skip: 0 })
            refetchSumData({ input: variables })
          })
        }}
        size="sm"
        round="true"
        icon="true"
        className="mr-2"
      >
        <i className="mdi mdi-cloud-upload" />
      </Button>
    )
  }

  const renderDownloadButton = () => {
    if (!data) {
      return <div />
    }

    return (
      <CSVLink filename={`${objectName}資料.csv`} separator={";"}>
        <Button size="sm" round="true" icon="true" className="mr-2">
          <i className="fas fa-download" />
        </Button>
      </CSVLink>
    )
  }

  const renderSettingPopover = () => {
    return (
      <Popover
        placement="bottom"
        isOpen={toggle}
        target="setting"
        fade={true}
        toggle={() => setToggle(!toggle)}
      >
        <PopoverBody style={{ fontSize: "0.9rem" }}>
          <div style={{ width: "200px" }}>
            <TableController
              objectName={Object.keys(fields)[0]}
              fieldsDef={fields[Object.keys(fields)[0]]}
              refresh={refresh}
              setRefresh={setRefresh}
            />
          </div>
        </PopoverBody>
      </Popover>
    )
  }

  const showConfirmModal = (callback) => {
    modal.reset()
    modal.setOnSubmit(callback)
    modal.setHeaderText("提示")
    modal.setModalContent(`確認是否要刪除${objectName}`)
    modal.toggle()
  }

  const renderDeleteButton = (id) => {
    return (
      <TrashButton
        onClick={() => {
          showConfirmModal(() => {
            deleteObject({
              variables: {
                id
              },
              refetchQueries: [{ query: queryObjectGql }]
            })
          })
        }}
      />
    )
  }

  const renderEditButton = (id) => {
    return (
      <div style={{ textAlign: "center" }}>
        <Link to={`${objectRoute}/detail/${id}`}>
          <Button size="sm" round="true" icon="true" color="inverse">
            <i className="fa fa-edit" />
          </Button>
        </Link>
      </div>
    )
  }

  const getSortedFields = (fields: Array, fieldsDef: Array) => {
    if (!fields || !hasSettingButton) {
      return fieldsDef
    }
    const sortedData = _(fields[Object.keys(fields)[0]])
      .filter({ show: true })
      .sortBy(["order"])
      .value()
    const sortedFieldsDef = sortedData.map((item) => {
      return fieldsDef.filter((def) => def.id === item.name)[0]
    })
    return sortedFieldsDef
  }

  const getColumnDefinitions = () => {
    const definitions = []
    definitions.push({
      Header: "id",
      accessor: "id",
      show: false
    })
    if (hasEditButton) {
      definitions.push({
        Header: "編輯",
        style: columStyle || defaultColumnStyle,
        width: 60,
        Cell: (cell) => {
          return renderEditButton(cell.row.id)
        }
      })
    }
    definitions.push(...getSortedFields(fields, fieldsDef))
    if (hasDeleteButton) {
      definitions.push({
        Header: "刪除",
        width: 60,
        style: columStyle || defaultColumnStyle,
        Cell: (cell) => {
          return renderDeleteButton(cell.row.id)
        }
      })
    }
    return definitions
  }

  const getReactTablePages = () => {
    return Math.ceil(sumData?.[Object.keys(sumData)[0]]?.total / take)
  }

  const getReactTablePage = () => {
    return pageIndex
  }

  useEffect(() => {
    if (pageIndex === 0) {
      return
    }

    setPageIndex(0)
  }, [sumData])

  useEffect(() => {
    if (!variables || Object.keys(variables).length === 0) {
      return
    }

    refetchSumData({ input: variables })
  }, [variables])

  if (loading) {
    return <Spinner />
  }

  return (
    <>
      {columnDefs && (
        <QueryPanel
          {...queryPanelProps}
          onQuery={() => {
            refetch({ take, skip: 0, input: variables })
            setPageIndex(0)
          }}
          onClear={() => {
            onClear()
            setPageIndex(0)
            refetch({ take, skip: 0, input: {} })
          }}
        />
      )}
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center content mb-3">
            <div>{hasCreateButton && renderCreateButton()}</div>
            <div>
              {hasUploadButton && uploadSettings && renderUploadButton()}
              {hasDownloadButton && renderDownloadButton()}
              {hasSettingButton && fieldsDefGql && renderSettingButton()}
            </div>
          </div>
          <ReactTable
            defaultPageSize={take || 10}
            onFilteredChange={() => {}}
            getTdProps={() => ({
              style: {
                display: "flex",
                flexDirection: "column",
                justifyContent: "center"
              }
            })}
            manual
            showPageJump={false}
            onFetchData={(state) => {
              getFetchData(state)
            }}
            pages={getReactTablePages() || 0}
            page={getReactTablePage() || 0}
            data={data?.[Object.keys(data)[0]]?.data || []}
            columns={[...getColumnDefinitions()]}
            showPagination={showPagination || true}
            showPageSizeOptions={false}
            noDataText={<span>目前表格沒有資料可以顯示</span>}
          >
            {(state, makeTable) => {
              // setSortedData(state.sortedData)
              return makeTable()
            }}
          </ReactTable>
          {hasSettingButton && fieldsDefGql && renderSettingPopover()}
        </CardBody>
      </Card>
    </>
  )
}
const defaultColumnStyle = { whiteSpace: "unset", textAlign: "center" }

CustomServerSideReactTableNew.propTypes = {
  queryObjectGql: PropTypes.object.isRequired,
  fieldsDef: PropTypes.array.isRequired,
  objectName: PropTypes.string,
  deleteObjectGql: PropTypes.object,
  fieldsDefGql: PropTypes.object,
  objectRoute: PropTypes.string,
  fieldsDefKey: PropTypes.string,
  hasCreateButton: PropTypes.bool,
  hasDeleteButton: PropTypes.bool,
  hasEditButton: PropTypes.bool,
  hasSettingButton: PropTypes.bool,
  hasUploadButton: PropTypes.bool,
  hasDownloadButton: PropTypes.bool,
  showPagination: PropTypes.bool,
  defaultPageSize: PropTypes.number,
  columnStyle: PropTypes.object,
  uploadSettings: PropTypes.object
}

export default withModal(CustomServerSideReactTableNew)
